import { Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Suspense, lazy, useEffect } from "react";
import { PageTransition } from "./PageTransition";
import { ScrollToTopButton } from "./ScrollToTopButton";
import { Loader2 } from "lucide-react";

// Lazy load all page components for code splitting
const Index = lazy(() => import("@/pages/Index"));
const SokMaklare = lazy(() => import("@/pages/SokMaklare"));
const PropertiesForSale = lazy(() => import("@/pages/PropertiesForSale"));
const PropertiesForRent = lazy(() => import("@/pages/PropertiesForRent"));
const PropertyDetail = lazy(() => import("@/pages/PropertyDetail"));
const Auth = lazy(() => import("@/pages/Auth"));
const Favorites = lazy(() => import("@/pages/Favorites"));
const Profile = lazy(() => import("@/pages/Profile"));
const AdminMapConfig = lazy(() => import("@/pages/AdminMapConfig"));
const AdminLocationManager = lazy(() => import("@/pages/AdminLocationManager"));
const Slutpriser = lazy(() => import("@/pages/Slutpriser"));
const News = lazy(() => import("@/pages/News"));
const NewsManagement = lazy(() => import("@/pages/NewsManagement"));
const NewsArticleDetail = lazy(() => import("@/pages/NewsArticleDetail"));
const CookiePolicy = lazy(() => import("@/pages/CookiePolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Press = lazy(() => import("@/pages/Press"));
const Investors = lazy(() => import("@/pages/Investors"));
const Advertising = lazy(() => import("@/pages/Advertising"));
const Jobs = lazy(() => import("@/pages/Jobs"));
const PropertyStatistics = lazy(() => import("@/pages/PropertyStatistics"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const NewsletterAdmin = lazy(() => import("@/pages/NewsletterAdmin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminNotifications = lazy(() => import("@/pages/AdminNotifications"));
const BrokerDashboard = lazy(() => import("@/pages/BrokerDashboard"));
const BrokerViewings = lazy(() => import("@/pages/BrokerViewings"));
const BrokerContacts = lazy(() => import("@/pages/BrokerContacts"));
const BrokerAnalytics = lazy(() => import("@/pages/BrokerAnalytics"));
const BrokerProfile = lazy(() => import("@/pages/BrokerProfile"));
const BrokerProperties = lazy(() => import("@/pages/BrokerProperties"));
const BrokerPricing = lazy(() => import("@/pages/BrokerPricing"));
const MaklarePublicProfile = lazy(() => import("@/pages/MaklarePublicProfile"));
const NewsletterPreferences = lazy(() => import("@/pages/NewsletterPreferences"));
const AgencyDashboard = lazy(() => import("@/pages/AgencyDashboard"));
const AgencyInviteAccept = lazy(() => import("@/pages/AgencyInviteAccept"));
const AgencyRegister = lazy(() => import("@/pages/AgencyRegister"));
const AgencyDirectory = lazy(() => import("@/pages/AgencyDirectory"));
const AgencyDetail = lazy(() => import("@/pages/AgencyDetail"));
const NotificationTest = lazy(() => import("@/pages/NotificationTest"));
const Settings = lazy(() => import("@/pages/Settings"));
const Messages = lazy(() => import("@/pages/Messages"));
const AdminAgencies = lazy(() => import("@/pages/AdminAgencies"));
const AgencyPricing = lazy(() => import("@/pages/AgencyPricing"));
const PublisherProfile = lazy(() => import("@/pages/PublisherProfile"));
const AdminContentReports = lazy(() => import("@/pages/AdminContentReports"));
const ModeratorDashboard = lazy(() => import("@/pages/ModeratorDashboard"));
const EmailTemplatesManager = lazy(() => import("@/pages/EmailTemplatesManager"));
const Leaderboard = lazy(() => import("@/pages/Leaderboard"));
const PublisherDashboard = lazy(() => import("@/pages/PublisherDashboard"));
const MyListings = lazy(() => import("@/pages/MyListings"));
const AdminSettings = lazy(() => import("@/pages/AdminSettings"));

// Iraq SEO pages
const IraqPage = lazy(() => import("@/pages/iraq/IraqPage"));
const GovernoratePage = lazy(() => import("@/pages/iraq/GovernoratePage"));
const PropertyTypePage = lazy(() => import("@/pages/iraq/PropertyTypePage"));

// Content pages
const ForeignersBuyingGuide = lazy(() => import("@/pages/guides/ForeignersBuyingGuide"));
const LegalGuide = lazy(() => import("@/pages/guides/LegalGuide"));
const MarketOverview = lazy(() => import("@/pages/guides/MarketOverview"));
const BuyingPropertyGuide = lazy(() => import("@/pages/guides/BuyingPropertyGuide"));
const PricesByCity = lazy(() => import("@/pages/guides/PricesByCity"));
const ApartmentsVsHouses = lazy(() => import("@/pages/guides/ApartmentsVsHouses"));

// Prefetch commonly visited pages after initial load
const prefetchRoutes = () => {
  // Use requestIdleCallback for non-blocking prefetch
  const prefetch = () => {
    import("@/pages/PropertiesForSale");
    import("@/pages/Slutpriser");
    import("@/pages/Auth");
    import("@/pages/About");
  };
  
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(prefetch);
  } else {
    setTimeout(prefetch, 2000);
  }
};

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

export function AnimatedRoutes() {
  const location = useLocation();

  // Prefetch common routes after initial render
  useEffect(() => {
    prefetchRoutes();
  }, []);

  return (
    <>
      <Suspense fallback={<PageLoader />}>
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<PageTransition><Index /></PageTransition>} />
            <Route path="/sok-maklare" element={<PageTransition><SokMaklare /></PageTransition>} />
            <Route path="/properties-for-sale" element={<PageTransition><PropertiesForSale /></PageTransition>} />
            <Route path="/properties-for-rent" element={<PageTransition><PropertiesForRent /></PageTransition>} />
            <Route path="/property/:id" element={<PageTransition><PropertyDetail /></PageTransition>} />
            <Route path="/auth" element={<PageTransition><Auth /></PageTransition>} />
            <Route path="/favorites" element={<PageTransition><Favorites /></PageTransition>} />
            <Route path="/profile" element={<PageTransition><Profile /></PageTransition>} />
            <Route path="/slutpriser" element={<PageTransition><Slutpriser /></PageTransition>} />
            <Route path="/news" element={<PageTransition><News /></PageTransition>} />
            <Route path="/news/:articleId" element={<PageTransition><NewsArticleDetail /></PageTransition>} />
            <Route path="/news/manage" element={<PageTransition><NewsManagement /></PageTransition>} />
            <Route path="/admin/map-config" element={<PageTransition><AdminMapConfig /></PageTransition>} />
            <Route path="/admin/locations" element={<PageTransition><AdminLocationManager /></PageTransition>} />
            <Route path="/cookies" element={<PageTransition><CookiePolicy /></PageTransition>} />
            <Route path="/terms" element={<PageTransition><TermsOfService /></PageTransition>} />
            <Route path="/privacy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
            <Route path="/about" element={<PageTransition><About /></PageTransition>} />
            <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
            <Route path="/press" element={<PageTransition><Press /></PageTransition>} />
            <Route path="/investors" element={<PageTransition><Investors /></PageTransition>} />
            <Route path="/advertising" element={<PageTransition><Advertising /></PageTransition>} />
            <Route path="/jobs" element={<PageTransition><Jobs /></PageTransition>} />
            <Route path="/statistics" element={<PageTransition><PropertyStatistics /></PageTransition>} />
            <Route path="/faq" element={<PageTransition><FAQ /></PageTransition>} />
            <Route path="/unsubscribe" element={<PageTransition><Unsubscribe /></PageTransition>} />
            <Route path="/newsletter/preferences" element={<PageTransition><NewsletterPreferences /></PageTransition>} />
            <Route path="/admin/newsletter" element={<PageTransition><NewsletterAdmin /></PageTransition>} />
            <Route path="/admin/notifications" element={<PageTransition><AdminNotifications /></PageTransition>} />
            <Route path="/admin" element={<PageTransition><AdminDashboard /></PageTransition>} />
            <Route path="/admin/settings" element={<PageTransition><AdminSettings /></PageTransition>} />
            <Route path="/broker" element={<PageTransition><BrokerDashboard /></PageTransition>} />
            <Route path="/broker/viewings" element={<PageTransition><BrokerViewings /></PageTransition>} />
            <Route path="/broker/contacts" element={<PageTransition><BrokerContacts /></PageTransition>} />
            <Route path="/broker/analytics" element={<PageTransition><BrokerAnalytics /></PageTransition>} />
            <Route path="/broker/profile" element={<PageTransition><BrokerProfile /></PageTransition>} />
            <Route path="/broker/properties" element={<PageTransition><BrokerProperties /></PageTransition>} />
            <Route path="/broker/pricing" element={<PageTransition><BrokerPricing /></PageTransition>} />
            <Route path="/settings" element={<PageTransition><Settings /></PageTransition>} />
            <Route path="/messages" element={<PageTransition><Messages /></PageTransition>} />
            <Route path="/maklare/:id" element={<PageTransition><MaklarePublicProfile /></PageTransition>} />
            <Route path="/agency" element={<PageTransition><AgencyDashboard /></PageTransition>} />
            <Route path="/agency/invite/:inviteCode" element={<PageTransition><AgencyInviteAccept /></PageTransition>} />
            <Route path="/agency/register/:inviteCode" element={<PageTransition><AgencyRegister /></PageTransition>} />
            <Route path="/agencies" element={<PageTransition><AgencyDirectory /></PageTransition>} />
            <Route path="/agencies/:agencyId" element={<PageTransition><AgencyDetail /></PageTransition>} />
            <Route path="/admin/notification-test" element={<PageTransition><NotificationTest /></PageTransition>} />
            <Route path="/admin/agencies" element={<PageTransition><AdminAgencies /></PageTransition>} />
            <Route path="/admin/content-reports" element={<PageTransition><AdminContentReports /></PageTransition>} />
            <Route path="/moderator" element={<PageTransition><ModeratorDashboard /></PageTransition>} />
            <Route path="/admin/email-templates" element={<PageTransition><EmailTemplatesManager /></PageTransition>} />
            <Route path="/agency-pricing" element={<PageTransition><AgencyPricing /></PageTransition>} />
            <Route path="/publisher/:publisherId" element={<PageTransition><PublisherProfile /></PageTransition>} />
            <Route path="/leaderboard" element={<PageTransition><Leaderboard /></PageTransition>} />
            <Route path="/publisher" element={<PageTransition><PublisherDashboard /></PageTransition>} />
            <Route path="/my-listings" element={<PageTransition><MyListings /></PageTransition>} />
            {/* Iraq SEO Pages */}
            <Route path="/iraq" element={<PageTransition><IraqPage /></PageTransition>} />
            <Route path="/iraq/:governorate" element={<PageTransition><GovernoratePage /></PageTransition>} />
            <Route path="/iraq/:governorate/:propertyType" element={<PageTransition><PropertyTypePage /></PageTransition>} />
            {/* Guide Pages */}
            <Route path="/guides/foreigners-buying-property" element={<PageTransition><ForeignersBuyingGuide /></PageTransition>} />
            <Route path="/guides/legal" element={<PageTransition><LegalGuide /></PageTransition>} />
            <Route path="/guides/market-overview" element={<PageTransition><MarketOverview /></PageTransition>} />
            <Route path="/guides/buying-property" element={<PageTransition><BuyingPropertyGuide /></PageTransition>} />
            <Route path="/guides/prices-by-city" element={<PageTransition><PricesByCity /></PageTransition>} />
            <Route path="/guides/apartments-vs-houses" element={<PageTransition><ApartmentsVsHouses /></PageTransition>} />
            <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
          </Routes>
        </AnimatePresence>
      </Suspense>
      <ScrollToTopButton />
    </>
  );
}
